import {
  DocumentTextIcon,
  DocumentMagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  DocumentPlusIcon,
  ReceiptPercentIcon,
  EnvelopeIcon,
  IdentificationIcon,
  ScaleIcon,
  HomeModernIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  DocumentDuplicateIcon,
  UserGroupIcon,
  BookOpenIcon,
  LightBulbIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

export interface NavigationItem {
  id: string;
  labelKey: string; // Translation key
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  enabled: boolean;
  badgeKey?: string; // Translation key for badge
  releaseQuarter?: string;
  descriptionKey?: string; // Translation key
}

export interface NavigationCategory {
  id: string;
  labelKey: string; // Translation key
  icon: React.ComponentType<{ className?: string }>;
  items: NavigationItem[];
  defaultExpanded?: boolean;
}

export const navigationCategories: NavigationCategory[] = [
  {
    id: 'general',
    labelKey: 'common:navigation.categories.general',
    icon: DocumentTextIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'questions',
        labelKey: 'common:navigation.items.askLegalQuestions',
        path: '/legal-questions',
        icon: QuestionMarkCircleIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.askLegalQuestions',
      },
      {
        id: 'legal-glossary',
        labelKey: 'common:navigation.items.legalGlossary',
        path: '/legal-glossary',
        icon: BookOpenIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.legalGlossary',
      },
      {
        id: 'deadline-calculator',
        labelKey: 'common:navigation.items.deadlineCalculator',
        path: '/deadline-calculator',
        icon: CalendarDaysIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.deadlineCalculator',
      },
      {
        id: 'review',
        labelKey: 'common:navigation.items.reviewDocuments',
        path: '/review-contract',
        icon: DocumentMagnifyingGlassIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.reviewDocuments',
      },
    ],
  },
  {
    id: 'create',
    labelKey: 'common:navigation.categories.create',
    icon: DocumentPlusIcon,
    defaultExpanded: true,
    items: [
      {
        id: 'will',
        labelKey: 'common:navigation.items.generateWill',
        path: '/generate-will',
        icon: DocumentTextIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.generateWill',
      },
      {
        id: 'bill-of-sale',
        labelKey: 'common:navigation.items.billOfSale',
        path: '/generate-bill-of-sale',
        icon: ReceiptPercentIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.billOfSale',
      },
      {
        id: 'legal-letters',
        labelKey: 'common:navigation.items.legalLetters',
        path: '/legal-letters',
        icon: EnvelopeIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.legalLetters',
      },
      {
        id: 'name-change',
        labelKey: 'common:navigation.items.nameChange',
        path: '/name-change',
        icon: IdentificationIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.nameChange',
      },
    ],
  },
  {
    id: 'legal-assistance',
    labelKey: 'common:navigation.categories.legalAssistance',
    icon: ScaleIcon,
    defaultExpanded: false,
    items: [
      {
        id: 'small-claims',
        labelKey: 'common:navigation.items.smallClaims',
        path: '/small-claims',
        icon: ScaleIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.smallClaims',
      },
      {
        id: 'tenant-rights',
        labelKey: 'common:navigation.items.tenantRights',
        path: '/tenant-rights',
        icon: HomeModernIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.tenantRights',
      },
      {
        id: 'employment-advisor',
        labelKey: 'common:navigation.items.employmentAdvisor',
        path: '/employment-advisor',
        icon: BriefcaseIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.employmentAdvisor',
      },
      {
        id: 'contract-negotiation',
        labelKey: 'common:navigation.items.contractNegotiation',
        path: '/contract-negotiation',
        icon: DocumentDuplicateIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.contractNegotiation',
      },
    ],
  },
  {
    id: 'business',
    labelKey: 'common:navigation.categories.business',
    icon: BuildingOfficeIcon,
    defaultExpanded: false,
    items: [
      {
        id: 'trademark-search',
        labelKey: 'common:navigation.items.trademarkSearch',
        path: '/trademark-search',
        icon: MagnifyingGlassIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.trademarkSearch',
      },
      {
        id: 'business-formation',
        labelKey: 'common:navigation.items.businessFormation',
        path: '/business-formation',
        icon: BuildingOfficeIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.businessFormation',
      },
      {
        id: 'business-contracts',
        labelKey: 'common:navigation.items.businessContracts',
        path: '/business-contracts',
        icon: DocumentDuplicateIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.businessContracts',
      },
    ],
  },
  {
    id: 'resources',
    labelKey: 'common:navigation.categories.resources',
    icon: BookOpenIcon,
    defaultExpanded: false,
    items: [
      {
        id: 'ai-advisor',
        labelKey: 'common:navigation.items.aiAdvisor',
        path: '/ai-advisor',
        icon: LightBulbIcon,
        enabled: true,
        descriptionKey: 'common:navigation.descriptions.aiAdvisor',
      },
    ],
  },
];

// Utility function to get all navigation items (flat list)
export const getAllNavigationItems = (): NavigationItem[] => {
  return navigationCategories.flatMap(category => category.items);
};

// Utility function to find navigation item by path
export const findNavigationItemByPath = (path: string): NavigationItem | undefined => {
  return getAllNavigationItems().find(item => item.path === path);
};

// Utility function to get category for a path
export const getCategoryForPath = (path: string): NavigationCategory | undefined => {
  return navigationCategories.find(category =>
    category.items.some(item => item.path === path)
  );
};
